import { ServiceLocator } from '@/services/ServiceLocator';
import { authState } from '@/interfaces/composables/auth/authState';
import type { RequestGoogleAuthFinish } from '@/infrastructure/api/client_typescript';

export async function checkAuth(
  username: string,
  password: string
): Promise<void> {
  const authService = ServiceLocator.getAuthService();
  const response = await authService.signInWithPassword(username, password);

  // store tokens via repository
  const tokenStore = ServiceLocator.getTokenStore();
  tokenStore.setAuthHack();
  tokenStore.setTokens(
    response.tokens.access_token,
    response.tokens.refresh_token
  );

  // Sync SessionManager's in-memory tokens so that token refresh
  // uses the sign-in refresh token instead of the stale guest token.
  ServiceLocator.getSessionManager().reloadTokensFromStore();
}

export async function ensureSession(): Promise<boolean> {
  const tokenStore = ServiceLocator.getTokenStore();
  const token = tokenStore.getAccess();
  if (!token) {
    authState.userName.value = 'Guest';
    authState.isAuthenticated.value = false;
    return false; // No token = not authenticated, require login
  }

  try {
    const authService = ServiceLocator.getAuthService();
    const user = await authService.getCurrentUser();

    const isValid =
      user && user.is_authenticated && (user.full_name || user.username);

    if (!isValid) throw new Error('Invalid user');

    authState.userName.value = user.full_name || user.username!;
    authState.isAuthenticated.value = true;
    return true;
  } catch {
    authState.userName.value = 'Guest';
    authState.isAuthenticated.value = false;
    return false; // Auth failed = require login
  }
}

export async function startGoogleAuth(): Promise<string> {
  const authService = ServiceLocator.getAuthService();
  const response = await authService.startGoogleAuth();
  return response.url;
}

export async function finishGoogleAuth(
  params: RequestGoogleAuthFinish
): Promise<void> {
  const authService = ServiceLocator.getAuthService();
  const response = await authService.finishGoogleAuth(params);

  const tokenStore = ServiceLocator.getTokenStore();
  tokenStore.setAuthHack();

  // Backend returns refresh_token but OpenAPI spec doesn't define it
  const tokens = response.tokens as { access_token: string; refresh_token?: string };
  tokenStore.setTokens(tokens.access_token, tokens.refresh_token || '');

  ServiceLocator.getSessionManager().reloadTokensFromStore();

  authState.userName.value = response.user.full_name || response.user.username;
  authState.isAuthenticated.value = true;
  authState.showLoginPopup.value = false;
}